import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FONTS, COLORS, BREAKPOINTS } from '../utils/styleConstants';
import { getProfile, createPaymentIntent, confirmPayment, validatePromoCode } from '../services/api';
import { useCredits } from '../context/CreditsContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import LoadingIndicator from '../components/LoadingIndicator';
import { parseApiAmount, formatAmountForDisplay } from '../utils/amountUtils';


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_dummy_key');

const STRIPE_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#A8C8E8',
      fontFamily: 'Work Sans, Avenir Next, sans-serif',
      '::placeholder': {
        color: '#A8C8E8',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const getPackageInfo = (packageData) => {
  const pricePerCredit = parseApiAmount(packageData.price_per_credit) || 0;
  const totalPrice = parseApiAmount(packageData.price) || 0;
  const credits = packageData.credits || 1;
  
  let shortName, name, type;
  
  switch (credits) {
    case 1:
      shortName = 'OV1';
      name = '1 Oferta';
      type = '1_offer';
      break;
    case 10:
      shortName = 'OV10';
      name = 'Pack 10 Ofertas';
      type = '10_offers';
      break;
    case 40:
      shortName = 'OV40';
      name = 'Pack 40 Ofertas';
      type = '40_offers';
      break;
    default:
      shortName = `OV${credits}`;
      name = `Pack ${credits} Ofertas`;
      type = `${credits}_offers`;
  }
  
  return {
    base: totalPrice,
    credits,
    name,
    shortName,
    pricePerOffer: formatAmountForDisplay(pricePerCredit).replace(' €', ''),
    type
  };
};

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.8rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    
    .left-section {
      grid-column: 1 / -1;
      margin-bottom: 1rem;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    
    .left-section {
      grid-column: 1;
      margin-bottom: 1rem;
    }
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionTitle = styled.h2`
  color: #A8C8E8;
  font-size: 2.8rem;
  font-weight: 300;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  line-height: 1.1;

  @media (max-width: 992px) {
    font-size: 2.2rem;
    margin: 0;
  }

  .bold {
    font-weight: 700;
  }
  
  .normal {
    font-weight: 300;
  }
`;

const PackageInfo = styled.div`
  background: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
`;

const PackageTitle = styled.h3`
  color: ${COLORS.SECONDARY};
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  font-family: ${FONTS.PRIMARY};
`;

const PackagePrice = styled.div`
  color: ${COLORS.SECONDARY};
  font-size: 1.1rem;
  font-weight: 400;
  margin: 0 0 0.5rem 0;
  font-family: ${FONTS.PRIMARY};
  
  .amount {
    font-size: 1.3rem;
    font-weight: 700;
  }
  
  .tax {
    color: #999;
    font-size: 0.9rem;
    font-weight: 400;
  }
`;

const DisclaimerText = styled.p`
  color: #A8C8E8;
  font-size: 0.8rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.4;
  margin: 0.5rem 0 0 0;
  text-align: left;
  font-weight: 300;
`;

const MiddleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: hidden;
  max-width: 100%;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
  overflow: hidden;
  max-width: 100%;
`;

const SectionHeader = styled.h3`
  color: ${COLORS.SECONDARY};
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;

  .input-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid #A8C8E8;

  &:focus-within {
    border-bottom-color: #A8C8E8;
  }

  &.error {
    border-bottom-color: #dc3545;
  }

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    border-bottom: 1px solid #A8C8E8;
  }
`;

const DoubleHorizontalField = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #A8C8E8;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const HalfField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;

  .input-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 2.5rem;
  }

  &.error {
    .input-row {
      border-bottom: 1px solid #dc3545;
    }
  }

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    border-bottom: 1px solid #A8C8E8;
  }
`;

const Label = styled.label`
  font-size: 0.85rem;
  margin-bottom: 0;
  color: #A8C8E8;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  flex: 1;
  text-align: right;
  margin-right: 1rem;
  color: #A8C8E8;
  min-width: 0;

  &::placeholder {
    color: #A8C8E8;
  }

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.85rem;
    width: 100%;
  }
`;

const Select = styled.select`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  flex: 1;
  text-align: right;
  margin-right: 1rem;
  cursor: pointer;
  color: #A8C8E8;
  min-width: 0;

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.85rem;
    width: 100%;
  }
`;

const StripeFieldWrapper = styled.div`
  flex: 1;
  padding: 0.25rem 0;
  text-align: left;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  // padding-bottom: 0.25rem;
  border-bottom: 1px solid #A8C8E8;
  align-items: end;
  
  &:focus-within {
    border-bottom-color: #A8C8E8;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #A8C8E8;
  }
`;

const PaymentField = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: end;
  
  @media (max-width: 768px) {
    padding-bottom: 0.25rem;
    border-bottom: 1px solid #bad1e6;
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  text-align: right;
`;

const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  justify-content: space-between;
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
  min-width: 16px;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  order: 2;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #A8C8E8;
  background-color: white;
  cursor: pointer;
  position: relative;
  
  &:checked {
    background-color: #A8C8E8;
    border-color: #A8C8E8;
  }
  
  &:checked::after {
    content: '✓';
    display: block;
    position: absolute;
    top: 0px;
    left: 1px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    line-height: 1;
  }
`;

const CheckboxLabel = styled.label`
  color: ${COLORS.SECONDARY};
  font-size: 0.6rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.4;
  flex: 1;
  order: 1;
  text-align: right;
  align-self: flex-end;
  
  a {
    color: ${COLORS.SECONDARY};
    text-decoration: underline;
  }
`;

const OrderSummary = styled.div`
  background: white;
  padding: 0;
`;

const SummaryTitle = styled.h4`
  color: ${COLORS.SECONDARY};
  font-size: 1rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-family: ${FONTS.SECONDARY};
  font-size: 0.95rem;
  color: ${COLORS.SECONDARY};
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.total {
    font-weight: 700;
    font-size: 1.1rem;
    color: ${COLORS.SECONDARY};
    // border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    // padding-top: 1rem;
    padding-bottom: 0.75rem;
    // margin-top: 0.5rem;
  }
  
  &.iva {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.5rem;
  }
  
  &.first {
    padding-top: 0;
  }
  
  .price {
    font-weight: 600;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Styled components para cupón de descuento
const CouponInputContainer = styled.div`
  margin-bottom: 1rem;
`;

const CouponLabel = styled.p`
  color: ${COLORS.PRIMARY};
  font-size: 1rem;
  font-family: ${FONTS.PRIMARY};
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const CouponInputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CouponInput = styled.input`
  flex: 1;
  padding: 0.5rem 0;
  border: none;
  border-bottom: 2px solid ${COLORS.PRIMARY};
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.PRIMARY};
  outline: none;
  background-color: transparent;
  transition: border-color 0.3s ease;
  text-transform: uppercase;

  &::placeholder {
    color: #A8C8E8;
    text-transform: none;
  }

  &:focus {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: transparent;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ApplyButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.hasText ? COLORS.ACCENT : 'white'};
  color: ${props => props.hasText ? 'white' : COLORS.ACCENT};
  border: 2px solid ${COLORS.ACCENT};
  border-radius: 1.5rem;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #dbe4f2;
  background-color: white;
  color: #a8b9d5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background-color: #f5f8fd;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.primary {
    background: white;
    color: ${COLORS.PRIMARY};
    border-color: ${COLORS.PRIMARY};
    
    &:hover:not(:disabled) {
      background: ${COLORS.PRIMARY};
      color: white;
      border-color: ${COLORS.PRIMARY};
    }
  }
`;

const WideInput = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  flex: 3; // Wider than regular inputs
  text-align: right;
  margin-right: 1rem;
  color: #A8C8E8;
  min-width: 250px; // Larger minimum width

  &::placeholder {
    color: #A8C8E8;
  }

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.85rem;
    width: 100%;
    min-width: unset;
  }
`;

const WideLabel = styled.label`
  font-size: 0.85rem;
  margin-bottom: 0;
  color: #A8C8E8;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  flex: 0 0 auto;
  min-width: 80px; // Smaller to give more space to input

  @media (max-width: 768px) {
    font-size: 0.85rem;
    width: 100%;
    margin-bottom: 0.5rem;
    min-width: unset;
  }
`;

const CityHalfField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;

  .input-row {
    display: flex;
    align-items: center;
    gap: 0.5rem; // Reduced gap to give more space to input
    height: 2.5rem;
  }

  &.error {
    .input-row {
      border-bottom: 1px solid #dc3545;
    }
  }

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    border-bottom: 1px solid #A8C8E8;
  }
`;

const CityLabel = styled.label`
  font-size: 0.85rem;
  margin-bottom: 0;
  color: #A8C8E8;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  flex: 0 0 auto;
  min-width: 60px; // Even smaller to maximize input space

  @media (max-width: 768px) {
    font-size: 0.85rem;
    width: 100%;
    margin-bottom: 0.5rem;
    min-width: unset;
  }
`;

const CityInput = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  outline: none;
  background-color: transparent;
  flex: 4; // Even more space than WideInput
  text-align: right;
  margin-right: 0.5rem; // Reduced margin
  color: #A8C8E8;
  min-width: 180px; // Specific width for city field

  &::placeholder {
    color: #A8C8E8;
  }

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.85rem;
    width: 100%;
    min-width: unset;
  }
`;

const provinces = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz', 'Barcelona',
  'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ciudad Real', 'Córdoba',
  'Cuenca', 'Girona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca', 'Jaén',
  'La Coruña', 'La Rioja', 'Las Palmas', 'León', 'Lleida', 'Lugo', 'Madrid', 'Málaga',
  'Murcia', 'Navarra', 'Ourense', 'Palencia', 'Pontevedra', 'Salamanca', 'Santa Cruz de Tenerife',
  'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid',
  'Vizcaya', 'Zamora', 'Zaragoza'
];

const BillingForm = ({ packageData, refreshCredits }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    billingEmail: '',
    company: '',
    nif: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'España',
    province: '',
    acceptMarketing: false,
    acceptTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  
  // Estados para cupón de descuento
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  useEffect(() => {
    loadUserProfile();
  }, []);
  
  const loadUserProfile = async () => {
    try {
      const profileData = await getProfile();
      const user = profileData.user;
      
      setFormData(prev => ({
        ...prev,
        name: user.first_name || '',
        surname: user.last_name || '',
        email: user.email || '',
        billingEmail: user.email || '',
        nif: user.id_number || '',
        province: user.province_name || ''
      }));
    } catch (error) {
      console.error('Error cargando perfil:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validar el campo específico inmediatamente
    const newErrors = { ...errors };
    
    if (field === 'nif') {
      const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
      const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
      const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/i;
      
      if (!value.trim() || (!nifRegex.test(value) && !nieRegex.test(value) && !cifRegex.test(value))) {
        newErrors.nif = 'NIF/NIE/CIF inválido';
      } else {
        delete newErrors.nif;
      }
    }
    
    if (field === 'address') {
      if (!value.trim() || value.length < 1) {
        newErrors.address = 'La dirección debe tener al menos 1 carácter';
      } else {
        delete newErrors.address;
      }
    }
    
    if (field === 'city') {
      if (!value.trim() || value.length < 2) {
        newErrors.city = 'La ciudad debe tener al menos 2 caracteres';
      } else {
        delete newErrors.city;
      }
    }
    
    if (field === 'postalCode') {
      const postalCodeRegex = /^[0-9]{5}$/;
      if (!value.trim() || !postalCodeRegex.test(value)) {
        newErrors.postalCode = 'Código postal inválido (5 dígitos)';
      } else {
        delete newErrors.postalCode;
      }
    }
    
    if (field === 'acceptTerms') {
      if (!value) {
        newErrors.acceptTerms = 'Debe aceptar las condiciones de contratación';
      } else {
        delete newErrors.acceptTerms;
      }
    }
    
    if (field === 'acceptMarketing') {
      if (!value) {
        newErrors.marketing = 'Debe aceptar la política de protección de datos';
      } else {
        delete newErrors.marketing;
      }
    }
    
    if (field === 'province') {
      if (!value.trim()) {
        newErrors.province = 'Debe seleccionar una provincia';
      } else {
        delete newErrors.province;
      }
    }
    
    setErrors(newErrors);
  };

  const handleCardNumberChange = (event) => {
    setCardNumberComplete(event.complete);
  };

  const handleCardExpiryChange = (event) => {
    setCardExpiryComplete(event.complete);
  };

  const handleCardCvcChange = (event) => {
    setCardCvcComplete(event.complete);
  };
  
  const isFormComplete = () => {
    // Verificar que no haya errores
    const hasErrors = Object.keys(errors).length > 0;
    
    // Verificar que todos los campos obligatorios estén completos
    const allFieldsComplete = (
      formData.name.trim() !== '' &&
      formData.surname.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.billingEmail.trim() !== '' &&
      formData.nif.trim() !== '' &&
      formData.address.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.postalCode.trim() !== '' &&
      formData.province.trim() !== '' &&
      formData.acceptTerms &&
      formData.acceptMarketing &&
      cardNumberComplete &&
      cardExpiryComplete &&
      cardCvcComplete
    );
    
    // Si hay cupón sin aplicar, no permitir pago
    if (couponCode && !appliedCoupon) {
      return false;
    }
    
    return !hasErrors && allFieldsComplete;
  };

  const renderFormErrors = () => {
    const errorMessages = [];
    
    if (errors.nif) errorMessages.push(errors.nif);
    if (errors.address) errorMessages.push(errors.address);
    if (errors.city) errorMessages.push(errors.city);
    if (errors.postalCode) errorMessages.push(errors.postalCode);
    if (errors.province) errorMessages.push(errors.province);
    if (errors.acceptTerms) errorMessages.push(errors.acceptTerms);
    if (errors.marketing) errorMessages.push(errors.marketing);
    if (errors.general) errorMessages.push(errors.general);
    
    // Error de cupón
    if (couponError) errorMessages.push(couponError);
    
    // Validar tarjeta de crédito
    if (!cardNumberComplete || !cardExpiryComplete || !cardCvcComplete) {
      errorMessages.push('Complete todos los campos de la tarjeta de crédito');
    }
    
    if (errorMessages.length === 0) return null;
    
    return (
      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        {errorMessages.map((error, index) => (
          <ErrorMessage key={index} style={{ display: 'block', marginBottom: '0.5rem' }}>
            {error}
          </ErrorMessage>
        ))}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};
    
    const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
    const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/i;
    
    if (!formData.nif.trim() || 
        (!nifRegex.test(formData.nif) && !nieRegex.test(formData.nif) && !cifRegex.test(formData.nif))) {
      newErrors.nif = 'NIF/NIE/CIF inválido';
    }
    
    if (!formData.address.trim() || formData.address.length < 1) {
      newErrors.address = 'La dirección debe tener al menos 1 carácter';
    }
    
    if (!formData.city.trim() || formData.city.length < 2) {
      newErrors.city = 'La ciudad debe tener al menos 2 caracteres';
    }
    
    const postalCodeRegex = /^[0-9]{5}$/;
    if (!formData.postalCode.trim() || !postalCodeRegex.test(formData.postalCode)) {
      newErrors.postalCode = 'Código postal inválido (5 dígitos)';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Debe aceptar las condiciones de contratación';
    }
    
    if (!formData.acceptMarketing) {
      newErrors.marketing = 'Debe aceptar la política de protección de datos';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const calculateTotal = () => {
    if (!packageData) return { basePrice: 0, discount: 0, discountedPrice: 0, iva: 0, total: 0 };
    
    const packageInfo = getPackageInfo(packageData);
    const basePrice = packageInfo.base;
    
    let discount = 0;
    let discountedPrice = basePrice;
    
    if (appliedCoupon) {
      if (appliedCoupon.percent_off) {
        discount = basePrice * (appliedCoupon.percent_off / 100);
      } else if (appliedCoupon.amount_off) {
        discount = appliedCoupon.amount_off / 100; // Stripe usa centavos
      }
      discountedPrice = basePrice - discount;
    }
    
    const iva = discountedPrice * 0.21;
    const total = discountedPrice + iva;
    
    return { basePrice, discount, discountedPrice, iva, total };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !stripe || !elements) {
      return;
    }
    
    setProcessing(true);
    
    try {
      // Crear Payment Intent
      // Enviar a nuestro backend (incluye tax_id para factura/Stripe Customer)
      const billingDetailsForApi = {
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        tax_id: (formData.nif || '').toUpperCase(),
        address: {
          line1: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: 'ES'
        }
      };

      // Enviar a Stripe Elements (NO incluir tax_id aquí)
      const billingDetailsForStripe = {
        name: `${formData.name} ${formData.surname}`,
        email: formData.email,
        address: {
          line1: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: 'ES'
        }
      };
      
      const packageInfo = getPackageInfo(packageData);
      const paymentIntentResponse = await createPaymentIntent(
        packageInfo.type, 
        billingDetailsForApi,
        appliedCoupon ? appliedCoupon.code : null
      );
      
      // Procesar pago con Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentResponse.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: billingDetailsForStripe
          }
        }
      );
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Confirmar pago en backend
      const confirmResponse = await confirmPayment(paymentIntent.id);
      
      // REFRESCAR CRÉDITOS DESDE EL BACKEND
      if (confirmResponse) {
        await refreshCredits();
      }
      
      // Redirigir a la página de éxito
      navigate('/purchase/success', { state: { paymentData: confirmResponse } });
      
    } catch (error) {
      console.error('Error procesando pago:', error);
      setErrors({ general: error.message });
    } finally {
      setProcessing(false);
    }
  };
  
  const handleBackClick = () => {
    navigate('/purchase');
  };
  
  // Función para validar y aplicar cupón
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    setCouponError('');
    
    try {
      const response = await validatePromoCode(couponCode.trim());
      setAppliedCoupon(response.coupon);
      setCouponError('');
    } catch (error) {
      setCouponError('Código inválido');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };
  
  const { basePrice, discount, discountedPrice, iva, total } = calculateTotal();
  const packageInfo = packageData ? getPackageInfo(packageData) : null;
  
  if (loading) {
    return <LoadingIndicator />;
  }
  
  if (!packageInfo) {
    return (
      <ErrorMessage style={{ textAlign: 'center', fontSize: '1rem', padding: '2rem' }}>
        Error: No se pudieron cargar los datos del paquete seleccionado.
      </ErrorMessage>
    );
  }
  
  return (
    <>
      <form onSubmit={handleSubmit}>
        <ContentWrapper>
          <LeftSection className="left-section">
            <SectionTitle>
              Has escogido<br />
              <span className="bold">el paquete</span> <span className="bold">{packageInfo.shortName}</span><br />
              <span className="normal">{packageInfo.pricePerOffer} €/of.</span>
            </SectionTitle>
            
            <PackageInfo>
              <PackageTitle>Total {packageInfo.name}</PackageTitle>
              <PackagePrice>
                <span className="amount">{formatAmountForDisplay(basePrice)}</span> <span className="tax">(+IVA)</span>
              </PackagePrice>
            </PackageInfo>
            
            <DisclaimerText>
              Los planes caducan pasados los 6 meses desde el momento del pago de los mismos 
              y no se admiten reembolsos en el caso de no haber agotado los expedientes.
            </DisclaimerText>
          </LeftSection>
          
          <MiddleSection>
            {/* Campos ocultos para mantener los datos personales */}
            <input type="hidden" value={formData.name} />
            <input type="hidden" value={formData.surname} />
            <input type="hidden" value={formData.email} />
            
            <FormSection>
              <SectionHeader>Datos tarjeta</SectionHeader>
              
              <PaymentGrid>
                <PaymentField>
                  <StripeFieldWrapper>
                    <CardNumberElement 
                      options={STRIPE_ELEMENT_OPTIONS} 
                      onChange={handleCardNumberChange}
                    />
                  </StripeFieldWrapper>
                </PaymentField>
                
                <PaymentField>
                  <StripeFieldWrapper>
                    <CardExpiryElement 
                      options={STRIPE_ELEMENT_OPTIONS} 
                      onChange={handleCardExpiryChange}
                    />
                  </StripeFieldWrapper>
                </PaymentField>
                
                <PaymentField>
                  <StripeFieldWrapper>
                    <CardCvcElement 
                      options={STRIPE_ELEMENT_OPTIONS} 
                      onChange={handleCardCvcChange}
                    />
                  </StripeFieldWrapper>
                </PaymentField>
              </PaymentGrid>
            </FormSection>
            
            <FormSection>
              <SectionHeader>Datos de facturación</SectionHeader>
              
              <HorizontalField>
                <div className="input-row">
                  <WideLabel>Empresa</WideLabel>
                  <WideInput
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                  />
                </div>
              </HorizontalField>
              
              <HorizontalField>
                <div className="input-row">
                  <Label>NIF/CIF</Label>
                  <Input
                    type="text"
                    value={formData.nif}
                    onChange={(e) => handleInputChange('nif', e.target.value.toUpperCase())}
                    required
                  />
                </div>
              </HorizontalField>
              
              <HorizontalField>
                <div className="input-row">
                  <WideLabel>Dirección</WideLabel>
                  <WideInput
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    required
                  />
                </div>
              </HorizontalField>
              
              <DoubleHorizontalField>
                <CityHalfField>
                  <div className="input-row">
                    <CityLabel>Ciudad</CityLabel>
                    <CityInput
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      required
                    />
                  </div>
                </CityHalfField>
                
                <HalfField>
                  <div className="input-row">
                    <Label>Código postal</Label>
                    <Input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      maxLength="5"
                      required
                    />
                  </div>
                </HalfField>
              </DoubleHorizontalField>
              
              <DoubleHorizontalField>
                <HalfField>
                  <div className="input-row">
                    <Label>País</Label>
                    <Select value={formData.country} disabled>
                      <option value="España">España</option>
                    </Select>
                  </div>
                </HalfField>
                
                <HalfField>
                  <div className="input-row">
                    <Label>Provincia</Label>
                    <Select
                      value={formData.province}
                      onChange={(e) => handleInputChange('province', e.target.value)}
                    >
                      <option value="">Seleccionar provincia</option>
                      {provinces.map(province => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </Select>
                  </div>
                </HalfField>
              </DoubleHorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <WideLabel>Mail de facturación</WideLabel>
                  <WideInput
                    type="email"
                    value={formData.billingEmail}
                    onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                  />
                </div>
              </HorizontalField>
            </FormSection>
          </MiddleSection>
          
          <RightSection>
            <FormSection>
              <CouponInputContainer>
                <CouponLabel>¿Tienes un código promocional?</CouponLabel>
                <CouponInputRow>
                  <CouponInput
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Introduce tu código"
                    disabled={!!appliedCoupon}
                  />
                  <ApplyButton
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={!couponCode || validatingCoupon || !!appliedCoupon}
                    hasText={!!couponCode}
                  >
                    {validatingCoupon ? 'Validando...' : appliedCoupon ? '✓ Aplicado' : 'Aplicar'}
                  </ApplyButton>
                </CouponInputRow>
              </CouponInputContainer>
            </FormSection>
            
            <FormSection>
              <OrderSummary>
                <SummaryTitle>Resumen del pedido</SummaryTitle>
                <SummaryLine className="first">
                  <span>Total {packageInfo.name}</span>
                  <span className="price">{formatAmountForDisplay(basePrice)}</span>
                </SummaryLine>
                
                {appliedCoupon && (
                  <SummaryLine style={{ color: COLORS.PRIMARY }}>
                    <span>Descuento (-{appliedCoupon.percent_off ? `${appliedCoupon.percent_off}%` : formatAmountForDisplay(appliedCoupon.amount_off / 100)} DTO. CÓDIGO)</span>
                    <span className="price">- {formatAmountForDisplay(discount)}</span>
                  </SummaryLine>
                )}
                
                <SummaryLine className="iva">
                  <span>IVA (21%)</span>
                  <span className="price">{formatAmountForDisplay(iva)}</span>
                </SummaryLine>
                <SummaryLine className="total">
                  <span>Importe total</span>
                  <span className="price">{formatAmountForDisplay(total)}</span>
                </SummaryLine>
              </OrderSummary>
            </FormSection>
            
            <FormSection>
              <CheckboxSection>
                <CheckboxWrapper>
                  <Checkbox
                    type="checkbox"
                    checked={formData.acceptMarketing}
                    onChange={(e) => handleInputChange('acceptMarketing', e.target.checked)}
                  />
                  <CheckboxLabel>
                    He leído y acepto la <a href="https://ofertasvinculantes.es/politica-de-privacidad" target="_blank" rel="noopener noreferrer">Política de privacidad</a>
                  </CheckboxLabel>
                </CheckboxWrapper>
                
                <CheckboxWrapper>
                  <Checkbox
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                  />
                  <CheckboxLabel>
                    He leído y acepto las <a href="https://ofertasvinculantes.es/condiciones-generales" target="_blank" rel="noopener noreferrer">Condiciones de contratación</a>
                  </CheckboxLabel>
                </CheckboxWrapper>
              </CheckboxSection>
            </FormSection>
            
            <ButtonContainer>
              <NavButton type="button" onClick={handleBackClick}>
                ← Atrás
              </NavButton>
              <NavButton 
                type="submit" 
                className="primary" 
                disabled={processing || !stripe || !isFormComplete()}
              >
                {processing ? 'Procesando...' : 'Completar Pago'}
              </NavButton>
            </ButtonContainer>
            
            {renderFormErrors()}
          </RightSection>
        </ContentWrapper>
      </form>
    </>
  );
};

const PurchaseBillingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { packageData } = location.state || {};
  const { credits, formatCreditsDisplay, refreshCredits } = useCredits();
  
  useEffect(() => {
    if (!packageData) {
      console.error('No packageData found, redirecting to purchase page');
      navigate('/purchase');
    }
  }, [packageData, navigate]);
  

  
  if (!packageData) {
    return <LoadingIndicator />;
  }
  
  console.log('PackageData received:', packageData);
  
  return (
    <Elements stripe={stripePromise}>
      <PageContainer>
        <Sidebar />
        <ContentArea>
          <FormHeader>
            <Title>Comprar Nuevas Ofertas</Title>
            <CreditsCounter>
              <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
              <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
            </CreditsCounter>
          </FormHeader>
          <BillingForm packageData={packageData} refreshCredits={refreshCredits} />
          <Footer />
        </ContentArea>
      </PageContainer>
    </Elements>
  );
};

export default PurchaseBillingPage; 